/**
 * Pontos de autoridade por atividade real do usuário.
 * 100 pts por mapa criado, 50 pts por ação concluída.
 * Eventos já lançados em gamification_transactions (authority:map|action:{id})
 * não entram de novo, para não somar duas vezes com o saldo gravado.
 */

import { supabase } from '../lib/supabaseClient';

export const AUTHORITY_MAP_POINTS = 100;
export const AUTHORITY_ACTION_POINTS = 50;

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export interface UncreditedAuthorityScore {
  points: number;
  maps: number;
  actions: number;
  projectsLed: number;
}

function withOrg<T extends { eq: (column: string, value: string) => T }>(query: T, organizationId: string): T {
  if (organizationId && organizationId !== 'dev-org' && UUID_RE.test(organizationId)) {
    return query.eq('organization_id', organizationId);
  }
  return query;
}

export async function loadUncreditedAuthorityScore(
  userId: string,
  organizationId: string,
): Promise<UncreditedAuthorityScore> {
  const empty = { points: 0, maps: 0, actions: 0, projectsLed: 0 };
  if (!userId) return empty;

  const [createdMaps, ownedMaps, completedActions, credits] = await Promise.all([
    withOrg(
      supabase.from('projects').select('id').eq('created_by', userId),
      organizationId,
    ),
    supabase.from('project_members').select('project_id').eq('user_id', userId).eq('role', 'owner'),
    withOrg(
      supabase
        .from('actions')
        .select('id')
        .eq('status', 'completed')
        .or(`assigned_to.eq.${userId},created_by.eq.${userId}`),
      organizationId,
    ),
    supabase
      .from('gamification_transactions')
      .select('description')
      .eq('user_id', userId)
      .like('description', 'authority:%'),
  ]);

  const credited = new Set(
    (credits.data ?? [])
      .map((row) => row.description)
      .filter((value): value is string => Boolean(value)),
  );

  const mapIds = new Set<string>();
  for (const row of createdMaps.data ?? []) {
    if (row.id) mapIds.add(row.id);
  }
  for (const row of ownedMaps.data ?? []) {
    if (row.project_id) mapIds.add(row.project_id);
  }

  let points = 0;
  let maps = 0;
  for (const id of mapIds) {
    if (credited.has(`authority:map:${id}`)) continue;
    maps += 1;
    points += AUTHORITY_MAP_POINTS;
  }

  const actionIds = new Set<string>();
  for (const row of completedActions.data ?? []) {
    if (row.id) actionIds.add(row.id);
  }

  let actions = 0;
  for (const id of actionIds) {
    if (credited.has(`authority:action:${id}`)) continue;
    actions += 1;
    points += AUTHORITY_ACTION_POINTS;
  }

  return { points, maps, actions, projectsLed: mapIds.size };
}
